import { useState, useEffect, useMemo } from "react";
import { CSpinner, CButton, CBadge } from "@coreui/react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useMutation } from "react-query";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
import { useCookie } from "../../../hooks/useCookie";
import { useToast } from "../../../hooks/useToast";
import apiService from "../../../service/apiService";
import BoxWrapper from "../../../components/ui/BoxWrapper";
import {
  ActivityStatus,
  FormMode,
  GeoMode,
  LatLng,
  PlacePoint,
} from "../../../helpers/enums";
import { colors } from "../../../config/theme";
import {
  ActivityPreview,
  buildGeoData,
  parseGeoData,
  StepIndicator,
} from "../__sections";
import { STEPS } from "../../../helpers/constants";
import { useCitiesQuery } from "../../../hooks/Cities/useCitiesQuery";
import {
  findNearestCity,
  normalizeItinerary,
  polygonCentroid,
  slugify,
  toLatLng,
} from "../../../helpers/general";
import { useSelectedPlace } from "../../../hooks/place/useSelectedPlace";
import { useAuth } from "../../../hooks/useAuth";
import { ActivityFormValues, activitySchema } from "../../../helpers/types";

import PickupDropoff from "../steps/step2PickupDropoff";
import Translations from "../steps/step3Translations";
import Gallery from "../steps/step7Gallery";
import Review from "../steps/review";
import Itineraries from "../steps/step4Itineraries";
import Schedules from "../steps/step5Schedules";
import Options from "../steps/step6Options";
import { useGalleryManager } from "../../../hooks/useGalleryManager";
import { useActivitiesQuery } from "../../../hooks/Activities/useActivitiesQuery";
import {
  buildActivityFormData,
  mapResToFormValues,
  mapGalleryResponse,
  getApiErrorMessage,
} from "../../../helpers/activityFormData";
import { deriveFurthestStep, validateStep } from "../../../helpers/stepsConfig";
import PaymentMethods from "../steps/step8PaymentMethods";

delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

export default function NewActivity({ mode = FormMode.CREATE }) {
  const { token } = useCookie("vToken");
  const { toastSuccess, toastError } = useToast();
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const stepFromUrl = Number(searchParams.get("step"));
  const initialStep =
    stepFromUrl >= 1 && stepFromUrl <= STEPS.length ? stepFromUrl : 1;

  const [formLoading, setFormLoading] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [currentStep, setCurrentStep] = useState(initialStep);
  const [furthestStep, setFurthestStep] = useState(initialStep);
  const [activeLangTab, setActiveLangTab] = useState(0);

  const { user, isAdmin } = useAuth();
  const { selectedPlace } = useSelectedPlace(user);

  const [selectedPlaceOption, setSelectedPlaceOption] = useState<Place | null>(
    null,
  );
  const [recordId, setRecordId] = useState<string | undefined>(
    mode === FormMode.UPDATE ? params?.id : undefined,
  );

  const gallery = useGalleryManager({
    activityId: params.id ?? recordId,
    token,
  });

  const [pickupCityValue, setPickupCityValue] = useState("");
  const [pickupGeoMode, setPickupGeoMode] = useState<GeoMode>("points");
  const [pickupPlaces, setPickupPlaces] = useState<PlacePoint[]>([]);
  const [pickupPolygon, setPickupPolygon] = useState<LatLng[]>([]);

  const [dropoffSameAsPickup, setDropoffSameAsPickup] = useState(false);
  const [dropoffCityValue, setDropoffCityValue] = useState("");
  const [dropoffGeoMode, setDropoffGeoMode] = useState<GeoMode>("points");
  const [dropoffPlaces, setDropoffPlaces] = useState<PlacePoint[]>([]);
  const [dropoffPolygon, setDropoffPolygon] = useState<LatLng[]>([]);

  const { Cities } = useCitiesQuery(true);

  const methods = useForm<ActivityFormValues>({
    resolver: yupResolver(activitySchema),
    mode: "onBlur",
    defaultValues: {
      title: "",
      status: ActivityStatus.DRAFT,
      placeId: "",
      partnerCommission: 0,
      translations: [
        {
          lang: "fr",
          title: "",
          description: "",
          shortDescription: "",
          includes: "",
          notIncludes: "",
          imprtMustKnow: "",
          imprtMustBring: "",
          imprtNotAllowed: "",
          notSuitedFor: "",
        },
      ],
      itineraries: [],
      pickupDropoff: {
        isPickupAvailable: false,
        isDropoffAvailable: false,
        pickupType: "",
        dropoffType: "",
        pickupGeojson: null,
        dropoffGeojson: null,
      },
      schedules: [],
      options: [],
      gallery: [],
      paymentMethods: [],
    },
  });

  const {
    handleSubmit,
    reset,
    trigger,
    watch,
    setValue,
    getValues,
  } = methods;


  useEffect(() => {
    if (!isAdmin) {
      const derivedPlaceId = selectedPlace?.id ?? user?.adminForPlace?.id;
      if (derivedPlaceId) {
        setValue("placeId", derivedPlaceId, { shouldValidate: true });
      }
    }
  }, [isAdmin, selectedPlace, user, setValue]);

  const { fetchedActivityById: data } = useActivitiesQuery(params?.id);

  useEffect(() => {
    if (!data) return;

    setRecordId(data.id || params?.id);
    reset(mapResToFormValues(data));
    setFurthestStep((f) => Math.max(f, deriveFurthestStep(data), initialStep));

    const validPlaceId = data.placeId || data.place?.id;

    if (validPlaceId) {
      setValue("placeId", validPlaceId, { shouldValidate: false });
    }

    setFurthestStep((f) => Math.max(f, deriveFurthestStep(data), initialStep));

    if (data.place) {
      setSelectedPlaceOption(data.place);
    }

    if (data.place) setSelectedPlaceOption(data.place);

    const pickupParsed = parseGeoData(data.pickupDropoff?.pickupGeojson);
    setPickupGeoMode(pickupParsed.mode);
    setPickupPlaces(pickupParsed.places);
    setPickupPolygon(pickupParsed.polygon);

    const dropoffParsed = parseGeoData(data.pickupDropoff?.dropoffGeojson);
    setDropoffGeoMode(dropoffParsed.mode);
    setDropoffPlaces(dropoffParsed.places);
    setDropoffPolygon(dropoffParsed.polygon);

    if (
      data.pickupDropoff?.isDropoffAvailable &&
      data.pickupDropoff?.dropoffGeojson &&
      JSON.stringify(data.pickupDropoff.dropoffGeojson) ===
        JSON.stringify(data.pickupDropoff.pickupGeojson)
    ) {
      setDropoffSameAsPickup(true);
    }

    gallery.setGalleryItems(mapGalleryResponse(data) as any);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const createMutation = useMutation(async (formData: FormData) => {
    return await apiService.MakePostRequest(
      "activities",
      formData,
      token,
      true,
    );
  });

  const updateMutation = useMutation(
    async ({
      id,
      formData,
      placeId,
    }: {
      id: string;
      formData: FormData;
      placeId: string;
    }) => {
      return await apiService.MakePutRequest(
        `activities/${id}?placeId=${encodeURIComponent(placeId)}`,
        formData,
        token,
        true,
      );
    },
  );

  const isFullyEditable =
    mode === FormMode.CREATE || data?.status !== ActivityStatus.DRAFT;

  const handleStepClick = (id: number) => {
    if (!isFullyEditable) return;

    if (id <= furthestStep) {
      goToStep(id);
    }
  };

  const goToStep = (step: number) => {
    setCurrentStep(step);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("step", String(step));
        return next;
      },
      { replace: true },
    );
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const geoState = {
    pickupGeoMode,
    pickupPlaces,
    pickupPolygon,
    dropoffGeoMode,
    dropoffPlaces,
    dropoffPolygon,
    dropoffSameAsPickup,
  };

  const applySavedResponse = (saved: any) => {
    if (!saved) return;
    reset(mapResToFormValues(saved), { keepDirty: false, keepErrors: false });
    setFurthestStep((f) => Math.max(f, deriveFurthestStep(saved)));
    gallery.setGalleryItems(mapGalleryResponse(saved) as any);
  };

  const handleSaveAndNext = async () => {
    if (currentStep === 3) {
      const itineraries = getValues("itineraries") || [];
      setValue(
        "itineraries",
        itineraries.map((item: any, index: number) =>
          normalizeItinerary(item, index),
        ),
        { shouldDirty: true, shouldValidate: false },
      );
    }

    const isValid = await validateStep(currentStep, trigger);
    if (!isValid) {
      toastError(
        currentStep === 3
          ? "Complétez chaque étape de l'itinéraire avant de continuer."
          : "Veuillez corriger les champs de cette étape.",
      );
      return;
    }

    const data = getValues();

    try {
      setIsSavingDraft(true);
      const formData = buildActivityFormData(
        data,
        geoState,
        { galleryItems: gallery.galleryItems },
        buildGeoData as any,
        data.status || ActivityStatus.DRAFT,
      );

      let res;
      if (recordId) {
        res = await updateMutation.mutateAsync({
          id: recordId,
          formData,
          placeId: data.placeId,
        });
      } else {
        res = await createMutation.mutateAsync(formData);
        const newId = res?.id || res?.data?.id;
        if (newId) {
          setRecordId(newId);
          navigate(
            `/activities/update/${newId}?step=${Math.min(
              currentStep + 1,
              STEPS.length,
            )}`,
            { replace: true },
          );
        }
      }

      applySavedResponse(res?.data || res);
    } catch (error) {
      toastError(getApiErrorMessage(error) ?? "Échec de l'enregistrement");
      return;
    } finally {
      setIsSavingDraft(false);
    }

    const next = Math.min(currentStep + 1, STEPS.length);
    setFurthestStep((f) => Math.max(f, next));
    goToStep(next);
  };

  const handlePrev = () => goToStep(Math.max(currentStep - 1, 1));

  const addPickupPlace = (lat: number, lng: number, name?: string) =>
    setPickupPlaces((prev) => [
      ...prev,
      {
        id: `p-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        lat,
        lng,
        name,
      },
    ]);
  const addDropoffPlace = (lat: number, lng: number, name?: string) =>
    setDropoffPlaces((prev) => [
      ...prev,
      {
        id: `d-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        lat,
        lng,
        name,
      },
    ]);
  const removePickupPlace = (id: string) =>
    setPickupPlaces((prev) => prev.filter((p) => p.id !== id));
  const removeDropoffPlace = (id: string) =>
    setDropoffPlaces((prev) => prev.filter((p) => p.id !== id));

  const pickupCityRaw = Cities.find((c) => c.label === pickupCityValue);
  const dropoffCityRaw = Cities.find((c) => c.label === dropoffCityValue);

  const pickupCity = useMemo(() => toLatLng(pickupCityRaw), [pickupCityRaw]);
  const dropoffCity = useMemo(() => toLatLng(dropoffCityRaw), [dropoffCityRaw]);

  useEffect(() => {
    if (mode !== FormMode.UPDATE || !Cities?.length || pickupCityValue) return;
    const referencePoint =
      pickupGeoMode === "zones"
        ? polygonCentroid(pickupPolygon)
        : pickupPlaces[0]
          ? { lat: pickupPlaces[0].lat, lng: pickupPlaces[0].lng }
          : null;
    if (!referencePoint) return;
    const nearestCity = findNearestCity(referencePoint, Cities);
    if (nearestCity) setPickupCityValue(nearestCity.label);
  }, [
    mode,
    Cities,
    pickupGeoMode,
    pickupPlaces,
    pickupPolygon,
    pickupCityValue,
  ]);

  useEffect(() => {
    if (mode !== FormMode.UPDATE || !Cities?.length || dropoffCityValue) return;
    const referencePoint =
      dropoffGeoMode === "zones"
        ? polygonCentroid(dropoffPolygon)
        : dropoffPlaces[0]
          ? { lat: dropoffPlaces[0].lat, lng: dropoffPlaces[0].lng }
          : null;
    if (!referencePoint) return;
    const nearestCity = findNearestCity(referencePoint, Cities);
    if (nearestCity) setDropoffCityValue(nearestCity.label);
  }, [
    mode,
    Cities,
    dropoffGeoMode,
    dropoffPlaces,
    dropoffPolygon,
    dropoffCityValue,
  ]);

  const onSubmit = async (data: any) => {
    if (currentStep !== STEPS.length) return;

    console.log("Submitting data:", data);

    try {
      setFormLoading(true);
      const formData = buildActivityFormData(
        data,
        geoState,
        { galleryItems: gallery.galleryItems },
        buildGeoData as any,
        ActivityStatus.PENDING_REVIEW,
      );

      let res;
      if (recordId) {
        res = await updateMutation.mutateAsync({
          id: recordId,
          formData,
          placeId: data.placeId,
        });
        toastSuccess("Activité mise à jour avec succès");
      } else {
        res = await createMutation.mutateAsync(formData);
        const newId = res?.id || res?.data?.id;
        if (newId) setRecordId(newId);
        toastSuccess("Activité créée avec succès");
      }

      setValue("status", ActivityStatus.PENDING_REVIEW, {
        shouldValidate: false,
      });
      navigate("/activities");
    } catch (error) {
      toastError(getApiErrorMessage(error) ?? "Échec de l'opération");
    } finally {
      setFormLoading(false);
    }
  };

  const watchedTitle = watch("title");
  const previewSlug =
    (data as { slug?: string } | null | undefined)?.slug ||
    (watchedTitle ? slugify(watchedTitle) : "");

  return (
    <div className="mt-4">
      <BoxWrapper>
        <section className="p-4">
          <div className="border-bottom mb-4 pb-3 d-flex justify-content-between align-items-center flex-wrap gap-3">
            <div>
              <h4 className="m-0">
                {recordId ? "Mise à jour" : "Création"} de l'activité
              </h4>
              {watchedTitle && (
                <div className="small text-muted mt-1">{watchedTitle}</div>
              )}
            </div>
            <div className="d-flex align-items-center gap-3">
              <ActivityPreview slug={previewSlug} />
              <CBadge
                shape="rounded-pill"
                style={{
                  backgroundColor: colors.primary,
                  color: "white",
                  padding: "0.5rem 0.9rem",
                  fontWeight: 600,
                }}
              >
                Étape {currentStep} / {STEPS.length}
              </CBadge>
            </div>
          </div>

          <StepIndicator
            currentStep={currentStep}
            furthestStep={isFullyEditable ? STEPS.length : furthestStep}
            onStepClick={handleStepClick}
            isFullyEditable={isFullyEditable}
          />

          <FormProvider {...methods}>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleSubmit(onSubmit)(e);
              }}
            >
              {/* {currentStep === 3 && (
                <BasicInfo
                  isAdmin={isAdmin}
                  selectedPlaceOption={selectedPlaceOption}
                  setSelectedPlaceOption={setSelectedPlaceOption}
                />
              )} */}

              {currentStep === 2 && (
                <PickupDropoff
                  Cities={Cities}
                  pickupCityValue={pickupCityValue}
                  setPickupCityValue={setPickupCityValue}
                  pickupGeoMode={pickupGeoMode}
                  setPickupGeoMode={setPickupGeoMode}
                  pickupPlaces={pickupPlaces}
                  addPickupPlace={addPickupPlace}
                  removePickupPlace={removePickupPlace}
                  pickupPolygon={pickupPolygon}
                  onPickupPolygonChange={setPickupPolygon}
                  pickupCity={pickupCity}
                  dropoffSameAsPickup={dropoffSameAsPickup}
                  setDropoffSameAsPickup={setDropoffSameAsPickup}
                  dropoffCityValue={dropoffCityValue}
                  setDropoffCityValue={setDropoffCityValue}
                  dropoffGeoMode={dropoffGeoMode}
                  setDropoffGeoMode={setDropoffGeoMode}
                  dropoffPlaces={dropoffPlaces}
                  addDropoffPlace={addDropoffPlace}
                  removeDropoffPlace={removeDropoffPlace}
                  dropoffPolygon={dropoffPolygon}
                  onDropoffPolygonChange={setDropoffPolygon}
                  dropoffCity={dropoffCity}
                />
              )}

              {currentStep === 1 && (
                <Translations
                  activeLangTab={activeLangTab}
                  setActiveLangTab={setActiveLangTab}
                  selectedPlaceOption={selectedPlaceOption}
                  setSelectedPlaceOption={setSelectedPlaceOption}
                />
              )}

              {currentStep === 3 && <Itineraries />}

              {currentStep === 4 && <Schedules />}

              {currentStep === 5 && <Options />}

              {currentStep === 6 && <PaymentMethods />}

              {currentStep === 7 && (
                <Gallery
                  galleryItems={gallery.galleryItems}
                  isDragOver={gallery.isDragOver}
                  setIsDragOver={gallery.setIsDragOver}
                  draggedIndex={gallery.draggedIndex}
                  fileInputRef={gallery.fileInputRef}
                  addFiles={gallery.addFiles}
                  removeFile={gallery.removeFile}
                  clearAll={gallery.clearAllGallery}
                  handleDrop={gallery.handleDrop}
                  handleItemDragStart={gallery.handleItemDragStart}
                  handleItemDragOver={gallery.handleItemDragOver}
                  handleItemDragEnd={gallery.handleItemDragEnd}
                />
              )}

              {currentStep === 8 && (
                <Review
                  goToStep={goToStep}
                  galleryItems={gallery.galleryItems}
                  pickupGeoMode={pickupGeoMode}
                  pickupPlaces={pickupPlaces}
                  pickupPolygon={pickupPolygon}
                  dropoffSameAsPickup={dropoffSameAsPickup}
                  dropoffGeoMode={dropoffGeoMode}
                  dropoffPlaces={dropoffPlaces}
                  dropoffPolygon={dropoffPolygon}
                />
              )}

              <div className="d-flex justify-content-between mt-5 pt-3 border-top">
                <CButton
                  type="button"
                  color="secondary"
                  variant="outline"
                  onClick={() => navigate("/activities")}
                  disabled={formLoading || isSavingDraft}
                >
                  Annuler
                </CButton>

                <div className="d-flex gap-2">
                  <CButton
                    type="button"
                    color="secondary"
                    variant="outline"
                    onClick={handlePrev}
                    disabled={currentStep === 1 || formLoading || isSavingDraft}
                  >
                    ← Précédent
                  </CButton>

                  {currentStep < STEPS.length ? (
                    <CButton
                      type="button"
                      disabled={isSavingDraft}
                      onClick={handleSaveAndNext}
                      style={{
                        backgroundColor: colors.yellow,
                        border: "none",
                        color: "#fff",
                      }}
                    >
                      {isSavingDraft ? (
                        <CSpinner size="sm" className="me-2" />
                      ) : null}
                      {currentStep === STEPS.length - 1
                        ? "Enregistrer et voir le récapitulatif →"
                        : "Enregistrer et continuer →"}
                    </CButton>
                  ) : (
                    <CButton
                      type="button"
                      disabled={formLoading}
                      onClick={handleSubmit(onSubmit)}
                      style={{
                        backgroundColor: colors.yellow,
                        border: "none",
                        color: "#fff",
                      }}
                    >
                      {formLoading ? (
                        <CSpinner size="sm" className="me-2" />
                      ) : null}
                      {recordId
                        ? "Mettre à jour l'activité"
                        : "Créer l'activité"}
                    </CButton>
                  )}
                </div>
              </div>
            </form>
          </FormProvider>
        </section>
      </BoxWrapper>
    </div>
  );
}
